import type {
  ColumnMapping,
  ParsedFile,
} from '~/pages/inventory-import/lib/types';
import { unitOrUndefined } from '~/pages/inventory-import/lib/importPayload';
import { MAX_ROWS } from '~/pages/inventory-import/lib/parseFile';
import {
  BUNDLES,
  TRUCKLOADS,
  UNSTATED,
  packSizeOf,
} from '~/pages/inventory-import/lib/stockCounts';
import { sourceKeyOf } from './sourceLabels';
import { isTerminal } from '~/api/documentExtraction';
import { ExtractionVerdict, UnitCode } from '~/graphql/graphql';
import type {
  ExtractedDocumentView,
  ExtractedItem,
} from '~/api/documentExtraction';

export interface ImportHandoff {
  parsedFile: ParsedFile;
  mapping: ColumnMapping;
  returnTo: string;
  /**
   * Ids of the items whose cell merged into a row another cell speaks for, so the table can say
   * which values a reviewer checks on screen are not the ones the import receives.
   */
  supersededItems: ReadonlySet<string>;
}

export const IMPORT_COLUMNS = [
  'Product',
  'Length',
  'Stock',
  'Unit',
  'Location',
] as const;

/** Fields the import columns are composed from; the table shows the rest as evidence. */
export const CONSUMED_FIELDS = new Set([
  'species',
  'dimension',
  'grade',
  'lengthFt',
  'lengthRaw',
  'quantityValue',
  'quantityUnit',
  'priceUnit',
  'description',
  'productCode',
  'originMill',
]);

export const EMPTY_CELL = '—';

export function importRowOf(item: ExtractedItem): Record<string, string> {
  return mergeRow([item]);
}

export function isFlagged(item: ExtractedItem): boolean {
  return item.verdict !== ExtractionVerdict.Verified;
}

/** A verified cell that names no product has nothing to import into. */
export function isUnnamed(item: ExtractedItem): boolean {
  return !isFlagged(item) && descriptorOf(item) === '';
}

export const IMPORT_MAPPING: ColumnMapping = {
  Product: 'descriptor',
  Length: 'length',
  Stock: 'stock',
  Unit: 'unit',
  Location: 'locationCode',
};

export function fieldOf(item: ExtractedItem, name: string): string {
  return item.fields.find((field) => field.name === name)?.value ?? '';
}

/** A catalogue row is named by its description and code; an offer by species, size and grade. */
export function descriptorOf(item: ExtractedItem): string {
  const catalogue = [fieldOf(item, 'description'), fieldOf(item, 'productCode')]
    .filter(Boolean)
    .join(' ');
  if (catalogue) {
    return catalogue;
  }
  return [
    fieldOf(item, 'species'),
    fieldOf(item, 'dimension'),
    fieldOf(item, 'grade'),
  ]
    .filter(Boolean)
    .join(' ');
}

export function lengthOf(item: ExtractedItem): string {
  const feet = fieldOf(item, 'lengthFt');
  return feet ? `${feet}'` : fieldOf(item, 'lengthRaw');
}

const UNIT_ALIASES: Record<string, UnitCode> = { pieces: UnitCode.Pcs };

function unitCodeOf(word: string): string {
  return unitOrUndefined(word) ?? UNIT_ALIASES[word] ?? '';
}

function priceUnitOf(item: ExtractedItem): string {
  const [, unit = ''] = fieldOf(item, 'priceUnit').split('_per_');
  return unitCodeOf(unit);
}

export function unitOf(item: ExtractedItem): string {
  return priceUnitOf(item) || unitCodeOf(fieldOf(item, 'quantityUnit'));
}

export function stockOf(item: ExtractedItem): string {
  return stockIn(item, unitOf(item));
}

const UNSTATED_UNITS = new Set(['', UNSTATED]);

function stockIn(item: ExtractedItem, unit: string): string {
  const quantityUnit = fieldOf(item, 'quantityUnit');
  const counted = unitCodeOf(quantityUnit);
  if (counted === '' && !UNSTATED_UNITS.has(quantityUnit)) {
    return '';
  }
  return counted === unit ? fieldOf(item, 'quantityValue') : '';
}

/**
 * A source row lists one offer once, however many cells it prints: a tally, a US$ price and a
 * Cdn$ price are three positions but one import row. Cells merge when they share the file, the
 * row, the product and the length; a matrix row, one length per cell, stays one row per cell, and
 * two attachments of one email never merge, since both can have a page p1.
 */
function rowKeyOf(item: ExtractedItem): string {
  return sourceKeyOf(
    item.documentId,
    sourceRowOf(item.positionKey),
    descriptorOf(item),
    lengthOf(item)
  );
}

/**
 * The backend serialises a position as `<unit>#<coordinate>`, escaping a `#` inside the unit key
 * as `\#` and a backslash as `\\`: a PDF cell's coordinate is `r<row>#c<column>`, a sheet cell's
 * `<column letters><row>`. Splitting on every `#` would read a sheet named `Q1#Prices` as its own
 * separator and give every cell on it the same row.
 */
function sourceRowOf(positionKey: string): string {
  const [unit, first = '', second = ''] = splitUnescaped(positionKey);
  if (/^r\d+$/.test(first) && /^c\d+$/.test(second)) {
    return `${unit}#${first}`;
  }
  const sheetRow = /^[A-Z]+(\d+)$/.exec(first)?.[1];
  return `${unit}#${sheetRow ?? first}`;
}

function splitUnescaped(serialised: string): string[] {
  const segments: string[] = [];
  let current = '';
  let escaped = false;

  for (const character of serialised) {
    if (escaped) {
      current += character;
      escaped = false;
    } else if (character === '\\') {
      escaped = true;
    } else if (character === '#') {
      segments.push(current);
      current = '';
    } else {
      current += character;
    }
  }
  segments.push(current);
  return segments;
}

/** The review converts this count once it knows the row's product, and so its truck capacity. */
function uncodedCountOf(
  cells: ExtractedItem[],
  product: string,
  unit: string
): { count: string; unit: string } | null {
  const counted = (item: ExtractedItem) =>
    fieldOf(item, 'quantityValue') !== '';
  const trucks = cells.find(
    (item) => counted(item) && fieldOf(item, 'quantityUnit') === TRUCKLOADS
  );
  if (trucks) {
    return { count: fieldOf(trucks, 'quantityValue'), unit: TRUCKLOADS };
  }
  const bare = cells.find(
    (item) => counted(item) && UNSTATED_UNITS.has(fieldOf(item, 'quantityUnit'))
  );
  if (!bare) return null;
  const count = fieldOf(bare, 'quantityValue');
  if (packSizeOf(product) !== null) return { count, unit: BUNDLES };
  return unit === '' ? { count, unit: UNSTATED } : null;
}

function mergeRow(cells: ExtractedItem[]): Record<string, string> {
  const unit =
    cells.map(priceUnitOf).find(Boolean) ??
    cells.map(unitOf).find(Boolean) ??
    '';
  const product = descriptorOf(cells[0]);
  const stocked =
    unit === '' ? undefined : cells.find((item) => stockIn(item, unit) !== '');
  const uncoded = stocked ? null : uncodedCountOf(cells, product, unit);
  const located =
    cells.find((item) => fieldOf(item, 'originMill') !== '') ?? cells[0];
  return {
    Product: product,
    Length: lengthOf(cells[0]),
    Stock: uncoded?.count ?? (stocked ? stockIn(stocked, unit) : ''),
    Unit: uncoded?.unit ?? unit,
    Location: fieldOf(located, 'originMill'),
  };
}

export function buildImportHandoff(
  fileName: string,
  returnTo: string,
  items: ExtractedItem[]
): ImportHandoff {
  const byRow = new Map<string, ExtractedItem[]>();
  for (const item of items) {
    if (isFlagged(item) || isUnnamed(item)) {
      continue;
    }
    const key = rowKeyOf(item);
    const cells = byRow.get(key);
    if (cells) {
      cells.push(item);
    } else {
      byRow.set(key, [item]);
    }
  }
  const rows: Record<string, string>[] = [];
  const supersededItems = new Set<string>();
  for (const cells of byRow.values()) {
    const merged = mergeRow(cells);
    rows.push(merged);
    for (const cell of cells) {
      const alone = mergeRow([cell]);
      if (IMPORT_COLUMNS.some((column) => alone[column] !== merged[column])) {
        supersededItems.add(cell.id);
      }
    }
  }

  return {
    parsedFile: { fileName, headers: [...IMPORT_COLUMNS], rows },
    mapping: IMPORT_MAPPING,
    returnTo,
    supersededItems,
  };
}

/** The import takes at most MAX_ROWS rows, the same cap a parsed file meets. */
export function exceedsImportLimit(handoff: ImportHandoff): boolean {
  return handoff.parsedFile.rows.length > MAX_ROWS;
}

export function importHandoffOf(
  document: ExtractedDocumentView | undefined,
  returnTo: string
): ImportHandoff | null {
  if (!document || !isTerminal(document.status)) {
    return null;
  }
  const handoff = buildImportHandoff(
    'extracted-document',
    returnTo,
    document.items
  );
  return handoff.parsedFile.rows.length > 0 ? handoff : null;
}
